import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * Modified behavior:
 * Instead of triggering the real Add to cart button, the sticky button now triggers
 * the main product form's "More payment options" button.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | HTMLElement | null} */
  #targetMorePaymentOptionsButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  connectedCallback() {
    super.connectedCallback();

    this.#ensureStickyButtonHoverStyle();
    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();
    this.#cacheMorePaymentOptionsButton();
    this.#updateStickyButtonLabel();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();

    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }

    if (this.#resetTimeout) {
      clearTimeout(this.#resetTimeout);
    }
  }

  /**
   * Adds a safe CSS override so the sticky Buy now button has inverted colors:
   * default = white background + black text,
   * hover/focus = black background + white text.
   */
  #ensureStickyButtonHoverStyle() {
    if (document.getElementById('sticky-buy-now-hover-style')) return;

    const style = document.createElement('style');
    style.id = 'sticky-buy-now-hover-style';
    style.textContent = `
      sticky-add-to-cart [ref="addToCartButton"],
      sticky-add-to-cart button[ref="addToCartButton"] {
        background-color: #ffffff !important;
        color: #000000 !important;
        border: 1px solid #000000 !important;
        opacity: 1 !important;
        filter: none !important;
        transition:
          background-color 160ms ease,
          color 160ms ease,
          border-color 160ms ease,
          filter 160ms ease,
          opacity 160ms ease !important;
      }

      sticky-add-to-cart [ref="addToCartButton"] *,
      sticky-add-to-cart button[ref="addToCartButton"] * {
        color: #000000 !important;
        fill: #000000 !important;
        stroke: #000000 !important;
      }

      sticky-add-to-cart [ref="addToCartButton"]:hover,
      sticky-add-to-cart button[ref="addToCartButton"]:hover,
      sticky-add-to-cart [ref="addToCartButton"]:focus-visible,
      sticky-add-to-cart button[ref="addToCartButton"]:focus-visible {
        background-color: #000000 !important;
        color: #ffffff !important;
        border-color: #000000 !important;
        opacity: 1 !important;
        filter: none !important;
      }

      sticky-add-to-cart [ref="addToCartButton"]:hover *,
      sticky-add-to-cart button[ref="addToCartButton"]:hover *,
      sticky-add-to-cart [ref="addToCartButton"]:focus-visible *,
      sticky-add-to-cart button[ref="addToCartButton"]:focus-visible * {
        color: #ffffff !important;
        fill: #ffffff !important;
        stroke: #ffffff !important;
      }
    `;

    document.head.appendChild(style);
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // Only show sticky bar if buy buttons have been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        // Check if the element is above the viewport (scrolled past) or below (not yet reached)
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          // Element is above viewport - show sticky bar
          this.#showStickyBar();
        }
        // If rect.top >= 0, element is below viewport - don't show sticky bar yet
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again
          const rect = buyButtonsBlock.getBoundingClientRect();
          // Only show if buy buttons are above the viewport (scrolled past)
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);

    this.#cacheMorePaymentOptionsButton();
  }

  /**
   * Finds the main product form's "More payment options" button.
   *
   * Shopify/themes may render it with slightly different selectors, so this tries
   * several common possibilities.
   */
  #cacheMorePaymentOptionsButton() {
    const productForm = this.#getProductForm();

    if (!productForm) {
      this.#targetMorePaymentOptionsButton = null;
      return;
    }

    this.#targetMorePaymentOptionsButton =
      productForm.querySelector('.shopify-payment-button__more-options') ||
      productForm.querySelector('[data-testid="sheet-open-button"]') ||
      productForm.querySelector('button[aria-label*="More payment options" i]') ||
      productForm.querySelector('button[aria-label*="more payment options" i]') ||
      productForm.querySelector('button[name="shopify_payment_button"]') ||
      productForm.querySelector('.shopify-payment-button button:not(.shopify-payment-button__button--unbranded)') ||
      productForm.querySelector('.shopify-payment-button button');
  }

  /**
   * Changes the visible sticky button wording from cart behavior to payment behavior.
   */
  #updateStickyButtonLabel() {
    const { addToCartButton } = this.refs;
    if (!addToCartButton) return;

    addToCartButton.setAttribute('aria-label', 'Buy now');

    const quantityDisplay = this.refs.quantityDisplay;
    const textNodes = [];

    const walker = document.createTreeWalker(addToCartButton, NodeFilter.SHOW_TEXT, {
      acceptNode: (node) => {
        if (!node.textContent || !node.textContent.trim()) {
          return NodeFilter.FILTER_REJECT;
        }

        if (
          quantityDisplay &&
          node.parentElement &&
          quantityDisplay.contains(node.parentElement)
        ) {
          return NodeFilter.FILTER_REJECT;
        }

        return NodeFilter.FILTER_ACCEPT;
      },
    });

    while (walker.nextNode()) {
      textNodes.push(walker.currentNode);
    }

    if (textNodes.length > 0) {
      textNodes[0].textContent = 'Buy now';

      for (let i = 1; i < textNodes.length; i++) {
        const text = textNodes[i].textContent?.trim().toLowerCase() || '';

        if (
          text.includes('add') ||
          text.includes('cart') ||
          text.includes('more payment') ||
          text.includes('payment options') ||
          text.includes('добав') ||
          text.includes('корз') ||
          text.includes('куп')
        ) {
          textNodes[i].textContent = '';
        }
      }
    } else {
      addToCartButton.textContent = 'Buy now';
    }
  }

  /**
   * Gets current variant ID from dataset or hidden product form input.
   * @returns {string | null}
   */
  #getCurrentVariantId() {
    if (this.dataset.currentVariantId) {
      return this.dataset.currentVariantId;
    }

    const productForm = this.#getProductForm();
    const variantInput = productForm?.querySelector('input[name="id"]');

    if (variantInput instanceof HTMLInputElement && variantInput.value) {
      return variantInput.value;
    }

    return null;
  }

  /**
   * Fallback checkout method when "More payment options" cannot be found.
   * Uses Shopify cart permalink format: /cart/VARIANT_ID:QUANTITY?checkout
   */
  #redirectToCheckoutFallback() {
    const variantId = this.#getCurrentVariantId();
    if (!variantId) return;

    const quantity = Math.max(1, this.#currentQuantity || 1);
    window.location.href = `/cart/${variantId}:${quantity}?checkout`;
  }

  // Public action handlers

  /**
   * Handles the sticky button click.
   * Instead of clicking Add to cart, it now clicks "More payment options".
   */
  handleAddToCartClick = async () => {
    const { addToCartButton } = this.refs;

    if (!addToCartButton || addToCartButton.disabled) return;

    this.#cacheMorePaymentOptionsButton();

    if (
      this.#targetMorePaymentOptionsButton instanceof HTMLElement &&
      !this.#targetMorePaymentOptionsButton.hasAttribute('disabled') &&
      this.#targetMorePaymentOptionsButton.getAttribute('aria-disabled') !== 'true'
    ) {
      this.#targetMorePaymentOptionsButton.click();
      return;
    }

    this.#redirectToCheckoutFallback();
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // Re-cache the More payment options button after morphing
    this.#cacheMorePaymentOptionsButton();

    if (variant == null) {
      this.#handleVariantUnavailable();
    }

    // Restore the current quantity display if needed
    this.#updateButtonText();
    this.#updateStickyButtonLabel();
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;

    this.dataset.currentVariantId = variantId;
    this.#cacheMorePaymentOptionsButton();
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');

    if (!selectedOptions) return;

    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete.
   * Kept for compatibility with the original event wiring.
   * @param {CustomEvent} _event - The cart event (unused)
   */
  #handleCartAddComplete = (_event) => {
    // No add-to-cart puppet behavior needed anymore.
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
    this.#updateStickyButtonLabel();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
    this.#updateStickyButtonLabel();
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods

  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');

    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}